import { z } from 'zod';

/**
 * Files the agent shows the user with `share_file`: screenshots and videos it
 * made in the sandbox. When the turn ends, cubic copies each file into the
 * attachments Blob store and saves what it stored on the turn
 * (`CodeSessionTurn.sharedFiles`), where the session page and the Slack thread
 * read it. No Node imports: the page reads these too.
 */

/** Per file: about two minutes of nonstop scrolling once the demo-video skill compresses the take. */
export const SHARED_FILE_MAX_BYTES = 25 * 1024 * 1024;

/** Per turn; later calls are kept as not shared, so the transcript still says so. */
export const SHARED_FILE_MAX_PER_TURN = 10;

/** A caption is one short line; a longer one is replaced by the file's name. */
export const SHARED_FILE_CAPTION_MAX_LENGTH = 200;

/** What a shared file may be, by the type its bytes read as. */
export const SHARED_FILE_TYPES = {
  png: 'image/png',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  mp4: 'video/mp4',
  webm: 'video/webm',
} as const;

export type SharedFileContentType = (typeof SHARED_FILE_TYPES)[keyof typeof SHARED_FILE_TYPES];

/** The extensions the plugin accepts before the turn ends; the bytes decide the type after. */
export const SHARED_FILE_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.mp4',
  '.webm',
] as const;

/** Whether `bytes` start with `signature` (bytes, or ASCII text) at `offset`. */
export function bytesStartWith(
  bytes: Uint8Array,
  signature: readonly number[] | string,
  offset = 0,
) {
  const expected =
    typeof signature === 'string' ? [...signature].map((char) => char.charCodeAt(0)) : signature;
  return expected.every((byte, index) => bytes[offset + index] === byte);
}

/**
 * The type a shared file's bytes read as, or null when they are not an image
 * or video cubic shows. The extension is never trusted: a text file named
 * `.png` stays in the sandbox, and nothing is served as a type it is not.
 */
export function sniffSharedFileType(bytes: Uint8Array): SharedFileContentType | null {
  if (bytesStartWith(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return SHARED_FILE_TYPES.png;
  }
  if (bytesStartWith(bytes, [0xff, 0xd8, 0xff])) {
    return SHARED_FILE_TYPES.jpeg;
  }
  if (bytesStartWith(bytes, 'GIF87a') || bytesStartWith(bytes, 'GIF89a')) {
    return SHARED_FILE_TYPES.gif;
  }
  if (bytesStartWith(bytes, 'RIFF') && bytesStartWith(bytes, 'WEBP', 8)) {
    return SHARED_FILE_TYPES.webp;
  }
  // ISO base media (MP4, MOV, M4V): a box size, then `ftyp`.
  if (bytesStartWith(bytes, 'ftyp', 4)) {
    return SHARED_FILE_TYPES.mp4;
  }
  // Matroska/WebM: the EBML header.
  if (bytesStartWith(bytes, [0x1a, 0x45, 0xdf, 0xa3])) {
    return SHARED_FILE_TYPES.webm;
  }
  return null;
}

export function isSharedVideoType(contentType: string): boolean {
  return contentType.startsWith('video/');
}

const sharedFileBase = {
  /** The `share_file` call it answers. */
  callId: z.string(),
  /** The file's name in the sandbox, without its directory. */
  name: z.string(),
  caption: z.string(),
};

const storedSharedFile = z.object({
  status: z.literal('shared'),
  ...sharedFileBase,
  id: z.string().guid(),
  contentType: z.string(),
  size: z.number().int().nonnegative(),
  displaySize: z
    .object({ width: z.number().int().positive(), height: z.number().int().positive() })
    .optional(),
});

const failedSharedFile = z.object({
  status: z.literal('failed'),
  ...sharedFileBase,
  /** A sentence for the user. */
  reason: z.string(),
});

/** A file saved on a turn: stored, or why it was not. */
export const codeTurnSharedFileSchema = z.discriminatedUnion('status', [
  storedSharedFile.extend({
    /** Where the bytes are in the attachments store: `<installationId>/<repositoryId>/<id>`. */
    pathname: z.string(),
  }),
  failedSharedFile,
]);
export type CodeTurnSharedFile = z.infer<typeof codeTurnSharedFileSchema>;

/** A shared file as turn events carry it and the page shows it: without where the server keeps the bytes. */
export const codeSharedFileSummarySchema = z.discriminatedUnion('status', [
  storedSharedFile,
  failedSharedFile,
]);
export type CodeSharedFileSummary = z.infer<typeof codeSharedFileSummarySchema>;

/** The shared files saved on a turn row; a value that does not parse counts as none. */
export function turnSharedFiles(value: unknown): CodeTurnSharedFile[] {
  const parsed = z.array(codeTurnSharedFileSchema).safeParse(value);
  return parsed.success ? parsed.data : [];
}

export function sharedFileSummary(file: CodeTurnSharedFile): CodeSharedFileSummary {
  if (file.status === 'failed') {
    return file;
  }
  const { pathname: _pathname, ...summary } = file;
  return summary;
}

/** The line the page and the Slack thread show for a file that was not shared. */
export function sharedFileFailureText(file: { name: string; reason: string }): string {
  return `Couldn't share ${file.name}. ${file.reason}`;
}
